import './index.css'

import React, { useContext } from 'react'

import { PDF_FILE } from '@disco/constants/FileTypes'
import { getFileTypeByExt } from '@disco/utils/file-utils'
import { getTrackArtwork } from '@disco/utils/tracks'
import { getOptimizedUrl } from '@disco/api/imgixClient'

import type { Track } from 'types/track'

import { NestingVersionsContext } from 'contexts/nesting-versions'
import Icon from 'components/ui/icon'

import FileIcon from './file-icon'

type Props = {
  baseClassName?: string
  size?: '160' | 'full'
  track: Track
  /**
   * Serve the artwork through imgix with a modern format and compression
   * (auto=format,compress, q=75). Opt-in so only catalog track pages are
   * affected and the other usages of this shared component are unchanged.
   * SLAM-2073.
   */
  optimized?: boolean
}

const TrackArtwork = ({
  size,
  track,
  baseClassName = 'TrackArtwork',
  optimized = false,
}: Props) => {
  const nestingVersionsContext = useContext(NestingVersionsContext)

  if (nestingVersionsContext.nesting) {
    return (
      <div className="TrackArtwork__NestedPlay">
        <Icon type="play" width={11} height={11} />
      </div>
    )
  }

  const fileType = getFileTypeByExt(track.ext)
  const rawArtwork = getTrackArtwork(track, size)
  const backgroundImage =
    optimized && rawArtwork
      ? getOptimizedUrl({
          image: rawArtwork,
          params: size === 'full' ? { w: 760 } : { w: 320 },
        })
      : rawArtwork
  return (
    <div
      className={baseClassName}
      style={{
        border:
          backgroundImage && fileType === PDF_FILE
            ? '1px solid var(--gray-a6)'
            : undefined,
      }}
    >
      <div
        className="TrackArtwork--image"
        style={{
          backgroundImage: backgroundImage
            ? `url(${backgroundImage})`
            : undefined,
        }}
      >
        {(!backgroundImage || track.virus) && (
          <FileIcon
            fileType={fileType}
            size={size === 'full' ? 'large' : 'small'}
            virus={track.virus}
          />
        )}
      </div>
    </div>
  )
}

export default React.memo<Props>(TrackArtwork)
